import { Level } from "@/server/backend-api/generated";
import { mcn } from "@/utils";
import React, { PropsWithChildren, SVGProps, useState } from "react";
import { getLevelString } from "@/server/backend-api/enumToText";
import { IconActionClose } from "@/components/Icons";

export interface GraphSubtopic {
  name: string;
  completedLOsByLevel: {
    [Level.LevelAExplorer]: number;
    [Level.LevelBExpert]: number;
    [Level.LevelCPioneer]: number;
  };
  isMotivated: boolean;
  url: string;
  id: string;
}

export interface CompetenceGraphProps {
  subtopics: GraphSubtopic[];
  showMobileFlipScreenTip?: boolean;
}

export const EMPTY_COMPETENCE_GRAPH_PROPS: CompetenceGraphProps = {
  subtopics: [],
};

export function CompetenceGraph(props: CompetenceGraphProps) {
  const [hasClosedTip, setHasClosedTip] = useState(false);
  const showTip = props.showMobileFlipScreenTip === true && !hasClosedTip;
  return (
    <>
      {showTip && (
        <div className="tablet:hidden w-full h-full flex flex-col gap-y-2 justify-center items-center">
          <div className="flex justify-end w-full">
            <button
              className="p-2 text-white"
              onClick={() => setHasClosedTip(true)}
            >
              <IconActionClose className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col gap-y-7 items-center justify-center">
            <FlipPhoneTip />
            <p className="text-style-body2 text-center">
              We recommend to turn your device to view graph
            </p>
          </div>
        </div>
      )}
      <div className={mcn(showTip && "hidden tablet:block")}>
        <CompetenceGraphContent {...props} />
      </div>
    </>
  );
}

function CompetenceGraphContent(props: CompetenceGraphProps) {
  const { subtopics } = props;
  const nrSubtopics = subtopics.length;
  const hasManySubtopics = nrSubtopics > 7;
  const minSize = hasManySubtopics ? 30 : 55;

  return (
    <div
      className="grid text-style-caption"
      style={{
        gridTemplateColumns: `30px minmax(80px, 1fr) repeat(${nrSubtopics}, minmax(${minSize}px, 1fr))`,
        gridTemplateRows: `repeat(4, minmax(60px, 1fr)) 30px`,
      }}
    >
      <div className="row-span-3 flex flex-col justify-center items-center gap-y-2">
        <span>Level</span>
        <LegendArrowUp />
      </div>
      <SubtopicsRow level={Level.LevelCPioneer} subtopics={subtopics} />
      <SubtopicsRow level={Level.LevelBExpert} subtopics={subtopics} />
      <SubtopicsRow level={Level.LevelAExplorer} subtopics={subtopics} />
      {/*Need one padding cell here to make sure the Level Legend is aligned.*/}
      <Cell />
      <LabelXRow subtopics={subtopics} />

      {/*Need one padding cell with span = 2 here to make sure the Competence Legend is aligned.*/}
      <Cell className="col-span-2" />
      <div
        style={{
          gridColumn: `span ${nrSubtopics} / span ${nrSubtopics}`,
        }}
        className="flex justify-center items-center gap-x-2"
      >
        <span>Competence</span>
        <LegendArrowRight />
      </div>
    </div>
  );
}

interface RowProps {
  level: Level;
  subtopics: GraphSubtopic[];
}

function SubtopicsRow(props: RowProps) {
  const { level, subtopics } = props;
  return (
    <>
      <Cell className="flex justify-center items-center border-r-2">
        <span className="text-style-caption">{getLevelString(level)}</span>
      </Cell>
      {subtopics.map((subtopic, idx) => (
        <SubtopicAndLevelCell key={idx} subtopic={subtopic} level={level} />
      ))}
    </>
  );
}

interface LabelXRowProps {
  subtopics: GraphSubtopic[];
}

function LabelXRow(props: LabelXRowProps) {
  const { subtopics } = props;
  return (
    <>
      <Cell className="flex justify-end">
        <div className="border-t-[3px] w-[10px]"></div>
      </Cell>
      {subtopics.map((subtopic, idx) => {
        const isManyTopics = subtopics.length >= 7;
        const hasLongWord = subtopic.name
          .split(/[\s-]/)
          .some((word) => word.length >= 15);
        return (
          <Cell className="border-t-[3px]" key={idx}>
            <div className="h-full w-full flex items-center justify-center">
              <span
                className={mcn(
                  "text-[10px] text-center max-h-10",
                  isManyTopics && "-rotate-45",
                  hasLongWord && "break-all"
                )}
              >
                <a className="block hover:no-underline overflow-hidden clamp-3-lines max-w-[80px] underline" href={subtopic.url} title={subtopic.name}>{subtopic.name}</a>
              </span>
            </div>
          </Cell>
        );
      })}
    </>
  );
}

interface CellProps {
  className?: string;
}

function Cell(props: PropsWithChildren<CellProps>) {
  const { className, children } = props;
  return <div className={mcn(className)}>{children}</div>;
}

interface SubtopicAndLevelCellProps {
  subtopic: GraphSubtopic;
  level: Level;
}

function SubtopicAndLevelCell(props: SubtopicAndLevelCellProps) {
  const { subtopic, level } = props;
  const isMotivated = subtopic.isMotivated;
  const nrCompletedLOs = subtopic.completedLOsByLevel[level];
  const hasCompletedLOs = nrCompletedLOs > 0;
  const hasCompletedManyLOs = nrCompletedLOs >= 5;
  const nrCompletedAboveLevel = getCompletedLOCountAboveLevel(subtopic, level);

  const isDarkGreen =
    (hasCompletedLOs && !hasCompletedManyLOs) || nrCompletedAboveLevel > 0;
  const isLightGreen = hasCompletedManyLOs;
  const isTop =
    (hasCompletedLOs && nrCompletedAboveLevel === 0 && !isMotivated) ||
    (isMotivated && level === Level.LevelCPioneer);

  const className = mcn("border-t-2 border-r-2");
  return (
    <Cell className={className}>
      <a
        href={`/browse?topicIds=${subtopic.id}&levelIds=${level}`}
        className={mcn(
          "w-full h-full flex justify-center items-center hover:bg-white/20",
          isMotivated && "bg-dotted-pattern",
          isDarkGreen && "bg-dark-green",
          isLightGreen && "bg-green-100 border border-blue",
          isTop && "rounded-t-full",
          isTop && isDarkGreen && "ring-2 ring-white"
        )}
        title="Show learning opportunities"
      >
        {hasCompletedLOs && (
          <div className="rounded-full w-5 h-5 flex items-center justify-center bg-blue">
            {nrCompletedLOs}
          </div>
        )}
      </a>
    </Cell>
  );
}

function FlipPhoneTip(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="156"
      height="205"
      viewBox="0 0 156 205"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M95 54.5C95 51.1863 92.3137 48.5 89 48.5L6 48.5C2.68629 48.5 3.20338e-08 51.1863 7.15493e-08 54.5L1.78873e-06 198.5C1.82825e-06 201.814 2.6863 204.5 6 204.5L89 204.5C92.3137 204.5 95 201.814 95 198.5L95 54.5ZM90 185.5L90 53.5L5 53.5L5 185.5L90 185.5Z"
        fill="#666666"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M141.549 126.017C140.454 126.16 139.683 127.164 139.826 128.26C140.787 135.605 140.337 146.748 135.965 156.256C131.655 165.629 123.542 173.45 108.847 174.58C107.746 174.665 106.921 175.626 107.006 176.728C107.091 177.829 108.052 178.653 109.154 178.568C125.478 177.313 134.763 168.444 139.6 157.927C144.374 147.544 144.819 135.592 143.792 127.741C143.649 126.646 142.645 125.874 141.549 126.017Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M141.78 130.812L131.44 143.737C130.75 144.6 129.492 144.74 128.629 144.049C127.767 143.359 127.627 142.101 128.317 141.238L139.452 127.319C140.667 125.801 142.983 125.821 144.17 127.361L154.897 141.266C155.572 142.141 155.41 143.397 154.535 144.071C153.66 144.746 152.404 144.584 151.73 143.709L141.78 130.812Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6 0.499999C2.68629 0.5 4.69686e-07 3.18629 1.04907e-06 6.5L1.55613e-05 89.5C1.61406e-05 92.8137 2.68631 95.5 6.00002 95.5L150 95.5C153.314 95.5 156 92.8137 156 89.5L156 6.49997C156 3.18626 153.314 0.499973 150 0.499974L6 0.499999ZM137 5.49998L5 5.5L5.00002 90.5L137 90.5L137 5.49998Z"
        fill="white"
      />
    </svg>
  );
}

function LegendArrowUp(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="9"
      height="136"
      viewBox="0 0 9 136"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4.85355 0.646439C4.65829 0.451187 4.34171 0.451187 4.14645 0.646439L0.964466 3.82843C0.769204 4.02368 0.769204 4.34027 0.964466 4.53554C1.15973 4.73079 1.47631 4.73079 1.67157 4.53554L4.5 1.70711L7.32843 4.53554C7.52369 4.73079 7.84027 4.73079 8.03553 4.53554C8.2308 4.34027 8.2308 4.02368 8.03553 3.82843L4.85355 0.646439ZM4 1L4 136H5L5 1H4Z"
        fill="white"
      />
    </svg>
  );
}

function LegendArrowRight() {
  return (
    <svg
      width="136"
      height="8"
      viewBox="0 0 136 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M135.354 4.35354C135.549 4.15828 135.549 3.8417 135.354 3.64643L132.172 0.464455C131.976 0.269192 131.66 0.269192 131.464 0.464455C131.269 0.659717 131.269 0.976299 131.464 1.17156L134.293 3.99999L131.464 6.82842C131.269 7.02368 131.269 7.34026 131.464 7.53552C131.66 7.73078 131.976 7.73078 132.172 7.53552L135.354 4.35354ZM135 3.49999L-4.37114e-08 3.5L4.37114e-08 4.5L135 4.49999L135 3.49999Z"
        fill="white"
      />
    </svg>
  );
}

function getCompletedLOCountAboveLevel(
  subtopic: GraphSubtopic,
  level: Level
): number {
  switch (level) {
    case Level.LevelAExplorer:
      return (
        subtopic.completedLOsByLevel[Level.LevelBExpert] +
        subtopic.completedLOsByLevel[Level.LevelCPioneer]
      );
    case Level.LevelBExpert:
      return subtopic.completedLOsByLevel[Level.LevelCPioneer];
    case Level.LevelCPioneer:
      return 0;
  }
}
